import { useQuery } from '@tanstack/react-query';
import { BookOpen, GraduationCap, Repeat, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  COMPETENCY_LABELS,
  OPL_STATUSES,
  OPL_TYPES,
  OPL_TYPE_LABELS,
  type CompetencyLevel,
  type OplType,
} from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Card,
  CardHeader,
  Callout,
  Drawer,
  EmptyState,
  Field,
  ProgressBar,
  Select,
  SkeletonRows,
  Stat,
  StatusPill,
} from '../../components/ui';
import { api, qs } from '../../lib/api';
import { formatDate, formatDateTime, humanise } from '../../lib/format';

/**
 * Learning intelligence.
 *
 * The distinctions this page must never blur (directive §21): viewing an OPL is
 * not competency, completing training is not certification, and a similar
 * machine is not a replication target. The server keeps them apart; this file
 * displays them apart, and never derives one from another.
 */

interface OplOverview {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  published: number;
  target: {
    min: number;
    max: number;
    actual: number;
    status: 'BELOW_TARGET' | 'WITHIN_TARGET' | 'ABOVE_TARGET' | 'UNAVAILABLE';
    basis: string;
  };
}

interface TrainingOverview {
  assigned: number;
  viewed: number;
  trainingCompleted: number;
  competent: number;
  needsRetraining: number;
  completedOrBetter: number;
  completionRate: number | null;
  competencyRate: number | null;
}

interface AmStepRow {
  stepNo: number;
  code: string;
  name: string;
  inPilot: boolean;
  operators: number;
  certified: number;
  inTraining: number;
  percent: number | null;
}

interface CertificationSummary {
  certified: number;
  operators: number;
  target: number;
  pilotSteps: number[];
  rule: string;
  certifiedOperators: Array<{ id: string; fullName: string; certifiedAt: string | null }>;
}

interface RetrainingRow {
  userId: string;
  operatorName: string;
  oplId: string;
  oplNo: string;
  oplTitle: string;
  competencyResult: string | null;
  competencyNotes: string | null;
  trainedAt: string | null;
  assessedByName: string | null;
}

interface LearningResponse {
  opls: OplOverview;
  training: TrainingOverview;
  amSteps: AmStepRow[];
  certification: CertificationSummary;
  retraining: RetrainingRow[];
}

interface MatrixRow {
  id: string;
  fullName: string;
  username: string;
  shift: string | null;
  lineName: string | null;
  steps: Array<{ stepNo: number; inPilot: boolean; level: CompetencyLevel; certified: boolean }>;
  oplsAssigned: number;
  oplsCompleted: number;
  oplsCompetent: number;
  oplsRetraining: number;
  isCertified: boolean;
}

interface Effectiveness {
  oplId: string;
  oplNo: string;
  title: string;
  oplType: OplType;
  status: string;
  publishedAt: string | null;
  state: 'MEASURED' | 'INSUFFICIENT_DATA' | 'NO_SCOPE';
  scope: string;
  window: { days: number; isDefault: boolean; description: string };
  before: number | null;
  after: number | null;
  daysSincePublished: number | null;
  trainingAssigned: number;
  trainingCompleted: number;
  trainingCompetent: number;
  sourceProblemId: string | null;
  sourceProblemTag: string | null;
  machineCode: string | null;
  componentName: string | null;
}

interface OperatorDetail {
  operator: MatrixRow;
  opls: Array<{
    opl_id: string;
    opl_no: string;
    title: string;
    opl_type: string;
    status: string;
    assigned_at: string | null;
    viewed_at: string | null;
    completed_at: string | null;
    competency_result: string | null;
    trainer_name: string | null;
  }>;
  steps: Array<{
    step_no: number;
    name: string;
    in_pilot: number;
    status: string | null;
    certified_at: string | null;
    level: CompetencyLevel;
  }>;
}

interface Replication {
  oplId: string;
  oplNo: string;
  currentMachines: Array<{ id: string; code: string }>;
  candidates: Array<{
    machineId: string;
    machineCode: string;
    machineName: string;
    signals: string[];
  }>;
  disclaimer: string;
}

const TRAINING_TONE: Record<string, 'ok' | 'warn' | 'alarm' | 'neutral' | 'info'> = {
  ASSIGNED: 'neutral',
  VIEWED: 'info',
  TRAINING_COMPLETED: 'info',
  COMPETENT: 'ok',
  NEEDS_RETRAINING: 'alarm',
};

export function LearningPage() {
  const [lineId, setLineId] = useState('');
  const [machineId, setMachineId] = useState('');
  const [oplType, setOplType] = useState('');
  const [competency, setCompetency] = useState('');
  const [amStep, setAmStep] = useState('');

  const [operatorId, setOperatorId] = useState<string | null>(null);
  const [replicationFor, setReplicationFor] = useState<Effectiveness | null>(null);

  const scope = {
    lineId: lineId || undefined,
    machineId: machineId || undefined,
    oplType: oplType || undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['analytics', 'learning', scope],
    queryFn: () => api.get<LearningResponse>(`/api/analytics/learning${qs(scope)}`),
  });

  const { data: matrix } = useQuery({
    queryKey: ['analytics', 'learning', 'matrix', scope, competency, amStep],
    queryFn: () =>
      api.get<{ steps: AmStepRow[]; operators: MatrixRow[] }>(
        `/api/analytics/learning/matrix${qs({
          ...scope,
          competency: competency || undefined,
          amStep: amStep || undefined,
        })}`,
      ),
  });

  const { data: effectiveness } = useQuery({
    queryKey: ['analytics', 'learning', 'effectiveness', machineId],
    queryFn: () =>
      api.get<{ window: { days: number; isDefault: boolean }; data: Effectiveness[] }>(
        `/api/analytics/learning/effectiveness${qs({ machineId: machineId || undefined })}`,
      ),
  });

  const { data: machines } = useQuery({
    queryKey: ['machines', 'picker'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; code: string; name: string }> }>('/api/machines'),
  });

  const { data: operatorDetail, isLoading: detailLoading } = useQuery({
    enabled: operatorId !== null,
    queryKey: ['analytics', 'learning', 'operator', operatorId],
    queryFn: () => api.get<OperatorDetail>(`/api/analytics/learning/operators/${operatorId}`),
  });

  const { data: replication, isLoading: replicationLoading } = useQuery({
    enabled: replicationFor !== null,
    queryKey: ['analytics', 'learning', 'replication', replicationFor?.oplId],
    queryFn: () =>
      api.get<Replication>(`/api/analytics/learning/replication/${replicationFor!.oplId}`),
  });

  return (
    <div>
      <PageHeader
        title="Learning"
        subtitle="One-Point Lessons, operator competency, and whether documented lessons are holding."
      />

      <div className="space-y-4 p-4 lg:p-6">
        <Card className="p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Machine">
              <Select value={machineId} onChange={(e) => setMachineId(e.target.value)}>
                <option value="">All machines</option>
                {machines?.data.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code} — {m.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="OPL type">
              <Select value={oplType} onChange={(e) => setOplType(e.target.value)}>
                <option value="">All types</option>
                {OPL_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {OPL_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="AM step">
              <Select value={amStep} onChange={(e) => setAmStep(e.target.value)}>
                <option value="">Any step</option>
                {[0, 1, 2, 3].map((n) => (
                  <option key={n} value={n}>
                    Step {n}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Training status">
              <Select value={competency} onChange={(e) => setCompetency(e.target.value)}>
                <option value="">Any status</option>
                <option value="ASSIGNED">Assigned</option>
                <option value="VIEWED">Viewed</option>
                <option value="TRAINING_COMPLETED">Training completed</option>
                <option value="COMPETENT">Competent</option>
                <option value="NEEDS_RETRAINING">Needs retraining</option>
              </Select>
            </Field>
          </div>
        </Card>

        {isLoading || !data ? (
          <SkeletonRows rows={4} />
        ) : (
          <>
            {/* --- OPLs ------------------------------------------------- */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat label="OPLs" value={data.opls.total} icon={BookOpen} />
              <Stat label="Published" value={data.opls.published} tone="ok" />
              <Stat
                label="Training assigned"
                value={data.training.assigned}
                icon={GraduationCap}
              />
              <Stat
                label="Operators certified"
                value={`${data.certification.certified} / ${data.certification.target}`}
                tone={
                  data.certification.certified >= data.certification.target ? 'ok' : 'warn'
                }
                icon={ShieldCheck}
                hint={`of ${data.certification.operators} operators`}
              />
            </div>

            {/*
              §3: the target is stated separately from the actual, and withheld
              entirely when the selected scope cannot support the comparison.
            */}
            <Callout
              tone={
                data.opls.target.status === 'UNAVAILABLE'
                  ? 'info'
                  : data.opls.target.status === 'WITHIN_TARGET' ||
                      data.opls.target.status === 'ABOVE_TARGET'
                    ? 'ok'
                    : 'warn'
              }
              title={
                data.opls.target.status === 'UNAVAILABLE'
                  ? 'Target comparison unavailable'
                  : `OPLs created: ${data.opls.target.actual} · pilot target ${data.opls.target.min}–${data.opls.target.max} · ${humanise(data.opls.target.status)}`
              }
            >
              {data.opls.target.basis}
            </Callout>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader title="By type" subtitle="The three OPL types in the proposal" />
                <ul className="divide-y divide-steel-100">
                  {OPL_TYPES.map((type) => (
                    <li key={type} className="flex items-center justify-between px-4 py-2">
                      <span className="text-sm text-steel-700">{OPL_TYPE_LABELS[type]}</span>
                      <span className="text-sm font-semibold tabular-nums text-steel-900">
                        {data.opls.byType[type] ?? 0}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card>
                <CardHeader
                  title="Pipeline"
                  subtitle="Draft → Submitted → In review → Approved → Published → Archived"
                />
                <ul className="divide-y divide-steel-100">
                  {OPL_STATUSES.map((status) => (
                    <li key={status} className="flex items-center justify-between px-4 py-2">
                      <span className="text-sm text-steel-700">{humanise(status)}</span>
                      <span className="text-sm font-semibold tabular-nums text-steel-900">
                        {data.opls.byStatus[status] ?? 0}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>

            {/* --- Training --------------------------------------------- */}
            <Card>
              <CardHeader
                title="Training"
                subtitle="Completing the training and being assessed competent are separate facts"
              />
              <div className="grid grid-cols-2 gap-3 p-4 lg:grid-cols-5">
                <Stat label="Assigned" value={data.training.assigned} />
                <Stat label="Viewed only" value={data.training.viewed} tone="info" />
                <Stat label="Completed" value={data.training.completedOrBetter} tone="info" />
                <Stat label="Competent" value={data.training.competent} tone="ok" />
                <Stat
                  label="Needs retraining"
                  value={data.training.needsRetraining}
                  tone={data.training.needsRetraining > 0 ? 'alarm' : 'ok'}
                />
              </div>
              <div className="border-t border-steel-100 px-4 py-3 text-xs text-steel-600">
                {data.training.completionRate === null ? (
                  <>No OPL training has been assigned in this scope, so there is no rate.</>
                ) : (
                  <>
                    Completion {data.training.completionRate}% · competency{' '}
                    {data.training.competencyRate}%. Opening a lesson is recorded as
                    &ldquo;viewed&rdquo; and never counts as either.
                  </>
                )}
              </div>
            </Card>

            {/* --- AM steps --------------------------------------------- */}
            <Card>
              <CardHeader
                title="AM step progress"
                subtitle={data.certification.rule}
              />
              <div className="space-y-3 p-4">
                {data.amSteps
                  .filter((step) => step.inPilot)
                  .map((step) =>
                    step.percent === null ? (
                      <div key={step.stepNo}>
                        <p className="text-sm font-semibold text-steel-900">
                          Step {step.stepNo} — {step.name}
                        </p>
                        {/* No operators in scope means no proportion exists. */}
                        <p className="text-xs text-steel-500">
                          No operator in scope, so there is no progress to report.
                        </p>
                      </div>
                    ) : (
                      <div key={step.stepNo}>
                        {/* The bar counts certified operators only. */}
                        <ProgressBar
                          label={`Step ${step.stepNo} — ${step.name}`}
                          value={step.certified}
                          total={step.operators}
                          tone={step.percent === 100 ? 'ok' : 'info'}
                        />
                        <p className="mt-0.5 text-xs text-steel-500">
                          {step.percent}% certified
                          {step.inTraining > 0
                            ? ` · ${step.inTraining} in training, not counted until certified`
                            : ''}
                        </p>
                      </div>
                    ),
                  )}
                <p className="pt-1 text-xs text-steel-500">
                  Steps 4–7 exist but sit outside the pilot and are never certified here.
                </p>
              </div>
            </Card>

            {/* --- Retraining ------------------------------------------- */}
            {data.retraining.length > 0 ? (
              <Card className="border-alarm-300">
                <CardHeader
                  title={`${data.retraining.length} assignment(s) need retraining`}
                  subtitle="The reason shown is the one the assessor recorded"
                />
                <ul className="divide-y divide-steel-100">
                  {data.retraining.map((row) => (
                    <li key={`${row.userId}-${row.oplId}`} className="px-4 py-3">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="text-sm font-semibold text-steel-900">
                          {row.operatorName}
                        </p>
                        <Link
                          to={`/opl/${row.oplId}`}
                          className="font-mono text-xs text-info-700 hover:underline"
                        >
                          {row.oplNo}
                        </Link>
                      </div>
                      <p className="mt-0.5 text-xs text-steel-600">{row.oplTitle}</p>
                      <p className="mt-0.5 text-xs text-steel-500">
                        Result: {row.competencyResult ?? 'not recorded'}
                        {row.competencyNotes ? ` — ${row.competencyNotes}` : ''}
                        {row.trainedAt ? ` · assessed ${formatDate(row.trainedAt)}` : ''}
                        {row.assessedByName ? ` by ${row.assessedByName}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}

            {/* --- Matrix ----------------------------------------------- */}
            <Card>
              <CardHeader
                title="Operator learning matrix"
                subtitle="Click an operator for their full record"
              />
              {!matrix?.operators.length ? (
                <EmptyState
                  icon={GraduationCap}
                  title="No operators match"
                  message="No operator in scope matches the current filter."
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead>
                      <tr className="border-b border-steel-200 text-left text-xs tracking-wide text-steel-500 uppercase">
                        <th className="px-4 py-2 font-semibold">Operator</th>
                        {[0, 1, 2, 3].map((n) => (
                          <th key={n} className="px-2 py-2 text-center font-semibold">
                            AM {n}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-right font-semibold">Assigned</th>
                        <th className="px-3 py-2 text-right font-semibold">Completed</th>
                        <th className="px-3 py-2 text-right font-semibold">Competent</th>
                        <th className="px-4 py-2 text-right font-semibold">Retraining</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-steel-100">
                      {matrix.operators.map((operator) => (
                        <tr
                          key={operator.id}
                          className="cursor-pointer hover:bg-steel-50"
                          onClick={() => setOperatorId(operator.id)}
                        >
                          <td className="px-4 py-2">
                            <span className="font-semibold text-steel-900">
                              {operator.fullName}
                            </span>
                            {operator.isCertified ? (
                              <Badge tone="ok" className="ml-2">
                                Certified
                              </Badge>
                            ) : null}
                            <span className="block text-xs text-steel-500">
                              {operator.lineName ?? 'No line'}
                              {operator.shift ? ` · shift ${operator.shift}` : ''}
                            </span>
                          </td>
                          {[0, 1, 2, 3].map((n) => {
                            const step = operator.steps.find((s) => s.stepNo === n);
                            return (
                              <td key={n} className="px-2 py-2 text-center">
                                {/*
                                  A tick means certified. Competency level is a
                                  separate, weaker fact and is shown as a dot.
                                */}
                                {step?.certified ? (
                                  <span
                                    className="text-ok-700"
                                    title={`Certified · ${COMPETENCY_LABELS[step.level]}`}
                                  >
                                    ✓
                                  </span>
                                ) : step && step.level !== 'NONE' ? (
                                  <span
                                    className="text-warn-600"
                                    title={`Not certified · ${COMPETENCY_LABELS[step.level]}`}
                                  >
                                    ●
                                  </span>
                                ) : (
                                  <span className="text-steel-300" title="Not started">
                                    ○
                                  </span>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-3 py-2 text-right tabular-nums">
                            {operator.oplsAssigned}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {operator.oplsCompleted}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-ok-700">
                            {operator.oplsCompetent}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {operator.oplsRetraining > 0 ? (
                              <span className="font-semibold text-alarm-600">
                                {operator.oplsRetraining}
                              </span>
                            ) : (
                              <span className="text-steel-400">0</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="px-4 py-2 text-xs text-steel-500">
                    ✓ certified · ● assessed but not certified · ○ not started
                  </p>
                </div>
              )}
            </Card>

            {/* --- Effectiveness ---------------------------------------- */}
            <Card>
              <CardHeader
                title="Did the lesson hold?"
                subtitle={
                  effectiveness
                    ? `Problem Tags in the ${effectiveness.window.days} days before publication compared with the ${effectiveness.window.days} days after${effectiveness.window.isDefault ? ' (default window — configurable in settings)' : ''}`
                    : 'Loading the observation window'
                }
              />
              {!effectiveness?.data.length ? (
                <EmptyState
                  icon={BookOpen}
                  title="No OPLs in scope"
                  message="No One-Point Lesson matches the current filter."
                />
              ) : (
                <ul className="divide-y divide-steel-100">
                  {effectiveness.data.map((row) => (
                    <li key={row.oplId} className="px-4 py-3">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="text-sm font-bold text-steel-900">
                          <Link
                            to={`/opl/${row.oplId}`}
                            className="font-mono text-info-700 hover:underline"
                          >
                            {row.oplNo}
                          </Link>{' '}
                          {row.title}
                        </p>
                        <span className="flex items-center gap-1.5">
                          <Badge tone="neutral">{OPL_TYPE_LABELS[row.oplType]}</Badge>
                          <StatusPill tone="neutral" label={humanise(row.status)} />
                        </span>
                      </div>

                      {row.state === 'MEASURED' ? (
                        <div className="mt-2 flex flex-wrap items-center gap-4">
                          <span className="text-sm">
                            <span className="text-steel-500">Before: </span>
                            <span className="font-bold tabular-nums text-steel-900">
                              {row.before}
                            </span>
                          </span>
                          <span className="text-sm">
                            <span className="text-steel-500">After: </span>
                            <span className="font-bold tabular-nums text-steel-900">
                              {row.after}
                            </span>
                          </span>
                          <span className="text-sm">
                            <span className="text-steel-500">Trained: </span>
                            <span className="font-semibold tabular-nums text-steel-900">
                              {row.trainingCompetent} competent of {row.trainingAssigned}{' '}
                              assigned
                            </span>
                          </span>
                        </div>
                      ) : (
                        <p className="mt-2 text-sm font-semibold text-steel-500">
                          {row.state === 'NO_SCOPE' ? 'Not measurable' : 'Insufficient data'}
                        </p>
                      )}

                      {/*
                        §11: the observation method sits beside the result, and
                        §10: no improvement percentage is computed from it.
                      */}
                      <p className="mt-1 text-xs text-steel-500">{row.scope}</p>
                      {row.state === 'MEASURED' ? (
                        <p className="mt-0.5 text-xs text-steel-400">
                          Counts of comparable Problem Tags either side of publication. ODR
                          does not attribute the difference to the lesson.
                        </p>
                      ) : null}

                      <div className="mt-1 flex flex-wrap gap-3 text-xs">
                        {row.sourceProblemId ? (
                          <Link
                            to={`/problems/${row.sourceProblemId}`}
                            className="font-semibold text-info-700 hover:underline"
                          >
                            From {row.sourceProblemTag} →
                          </Link>
                        ) : null}
                        <button
                          type="button"
                          className="font-semibold text-info-700 hover:underline"
                          onClick={() => setReplicationFor(row)}
                        >
                          Potential replication →
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}
      </div>

      {/* --- Operator detail ---------------------------------------------- */}
      <Drawer
        open={operatorId !== null}
        onClose={() => setOperatorId(null)}
        width="lg"
        title={operatorDetail?.operator.fullName ?? 'Operator'}
        subtitle={
          operatorDetail
            ? operatorDetail.operator.isCertified
              ? 'Certified on every pilot AM step'
              : 'Not yet certified on all pilot AM steps'
            : ''
        }
      >
        {detailLoading || !operatorDetail ? (
          <SkeletonRows rows={5} />
        ) : (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-bold text-steel-900">AM progress</h3>
              <ul className="mt-1 divide-y divide-steel-100">
                {operatorDetail.steps.map((step) => (
                  <li
                    key={step.step_no}
                    className="flex items-center justify-between gap-2 py-2 text-sm"
                  >
                    <span className={step.in_pilot ? 'text-steel-800' : 'text-steel-400'}>
                      Step {step.step_no} — {step.name}
                      {step.in_pilot ? '' : ' (outside pilot)'}
                    </span>
                    <span className="shrink-0 text-xs">
                      {step.status === 'CERTIFIED' ? (
                        <Badge tone="ok">
                          Certified{step.certified_at ? ` ${formatDate(step.certified_at)}` : ''}
                        </Badge>
                      ) : step.status ? (
                        <Badge tone="info">{humanise(step.status)}</Badge>
                      ) : (
                        <span className="text-steel-400">Not started</span>
                      )}
                      <span className="ml-2 text-steel-500">
                        {COMPETENCY_LABELS[step.level]}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-4 gap-2 text-center">
              <Stat label="Assigned" value={operatorDetail.operator.oplsAssigned} />
              <Stat label="Completed" value={operatorDetail.operator.oplsCompleted} />
              <Stat label="Competent" value={operatorDetail.operator.oplsCompetent} tone="ok" />
              <Stat
                label="Retraining"
                value={operatorDetail.operator.oplsRetraining}
                tone={operatorDetail.operator.oplsRetraining > 0 ? 'alarm' : 'neutral'}
              />
            </div>

            <div>
              <h3 className="text-sm font-bold text-steel-900">Lessons</h3>
              {operatorDetail.opls.length === 0 ? (
                <p className="mt-1 text-sm text-steel-500">
                  No One-Point Lesson has been assigned to this operator.
                </p>
              ) : (
                <ul className="mt-1 divide-y divide-steel-100">
                  {operatorDetail.opls.map((opl) => (
                    <li key={opl.opl_id} className="py-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <Link
                          to={`/opl/${opl.opl_id}`}
                          className="text-sm font-semibold hover:underline"
                        >
                          <span className="font-mono text-info-700">{opl.opl_no}</span>{' '}
                          {opl.title}
                        </Link>
                        <Badge tone={TRAINING_TONE[opl.status] ?? 'neutral'}>
                          {humanise(opl.status)}
                        </Badge>
                      </div>
                      <p className="mt-0.5 text-xs text-steel-500">
                        {opl.viewed_at ? `Viewed ${formatDateTime(opl.viewed_at)}` : 'Not viewed'}
                        {opl.completed_at
                          ? ` · completed ${formatDate(opl.completed_at)}`
                          : ' · not completed'}
                        {opl.competency_result ? ` · assessed ${opl.competency_result}` : ''}
                        {opl.trainer_name ? ` by ${opl.trainer_name}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Drawer>

      {/* --- Replication --------------------------------------------------- */}
      <Drawer
        open={replicationFor !== null}
        onClose={() => setReplicationFor(null)}
        width="md"
        title={`Potential replication · ${replicationFor?.oplNo ?? ''}`}
        subtitle={replicationFor?.title}
      >
        {replicationLoading || !replication ? (
          <SkeletonRows rows={4} />
        ) : (
          <div className="space-y-4">
            <Callout tone="warn" title="Nothing has been applied">
              {replication.disclaimer}
            </Callout>

            <div>
              <h3 className="text-sm font-bold text-steel-900">Currently linked to</h3>
              <p className="mt-1 text-sm text-steel-700">
                {replication.currentMachines.length === 0
                  ? 'No machine'
                  : replication.currentMachines.map((m) => m.code).join(', ')}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-bold text-steel-900">Potentially relevant</h3>
              {replication.candidates.length === 0 ? (
                <p className="mt-1 text-sm text-steel-500">
                  No other machine shares a component, checklist task, problem category or root
                  cause with this lesson. Machines of the same type are not suggested on
                  similarity alone.
                </p>
              ) : (
                <ul className="mt-1 divide-y divide-steel-100">
                  {replication.candidates.map((candidate) => (
                    <li key={candidate.machineId} className="py-2">
                      <Link
                        to={`/machines/${candidate.machineId}`}
                        className="text-sm font-semibold hover:underline"
                      >
                        {candidate.machineCode}
                        <span className="ml-1 font-normal text-steel-500">
                          {candidate.machineName}
                        </span>
                      </Link>
                      <ul className="mt-0.5 text-xs text-steel-600">
                        {candidate.signals.map((signal) => (
                          <li key={signal}>• {signal}</li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <p className="text-xs text-steel-500">
              To apply this lesson elsewhere, open the OPL and link the machine there.
            </p>
          </div>
        )}
      </Drawer>
    </div>
  );
}
